import * as React from 'react';

import { Button } from '@/componentes/ui/button';

/* Barreira de erro — o que aparece quando o render lança.
 *
 * Sem ela, qualquer exceção de render derrubava a árvore inteira e o usuário
 * ficava com uma tela BRANCA: sem mensagem, sem saída, e sem nada que diga que
 * o trabalho dele continua salvo. Numa ferramenta em que a pessoa acabou de
 * configurar custos em cinco etapas, tela branca lê como perda de tudo.
 *
 * Classe e não função porque `componentDidCatch` não tem equivalente em hook.
 * É a única classe do repositório, e é por isso.
 *
 * O que ela NÃO faz: engolir o erro. Ele vai para o console, porque quem
 * depura precisa da pilha, e o `reset` volta a montar a árvore em vez de
 * recarregar a página, para não perder o que já está em memória.
 */

type Props = {
  children: React.ReactNode;
  /** Some do estado de erro quando isto muda: trocar de rota tenta de novo. */
  chave?: string;
};

type Estado = { erro: Error | null };

export class BarreiraDeErro extends React.Component<Props, Estado> {
  override state: Estado = { erro: null };

  static getDerivedStateFromError(erro: Error): Estado {
    return { erro };
  }

  override componentDidUpdate(anterior: Props): void {
    if (this.state.erro !== null && anterior.chave !== this.props.chave) {
      this.setState({ erro: null });
    }
  }

  override componentDidCatch(erro: Error, info: React.ErrorInfo): void {
    console.error('Erro de render capturado pela barreira:', erro, info.componentStack);
  }

  override render(): React.ReactNode {
    const { erro } = this.state;
    if (erro === null) return this.props.children;

    return (
      <div className="grid min-h-screen place-items-center p-6">
        <div
          role="alert"
          className="bg-card border-border grid max-w-md gap-3 rounded-xl border p-6 text-center"
        >
          <h1 className="text-secao">Esta tela não abriu</h1>
          <p className="text-muted-foreground text-apoio">
            Os dados que você já salvou estão no servidor e não foram perdidos. Tente abrir de novo,
            e se continuar acontecendo, volte ao painel.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <Button onClick={() => this.setState({ erro: null })}>Tentar de novo</Button>
            <Button
              variant="outline"
              onClick={() => {
                window.location.href = '/';
              }}
            >
              Ir para o painel
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
